import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { TaxReportDownloadDropdown } from '@/modules/issuer-dashboard/components/tax-reports/TaxReportDownloadDropdown.tsx';
import { buildHolderAvatarNameParts } from '@/modules/issuer-dashboard/helpers/taxReportHolderAvatar.ts';
import { useGenerateTaxReportDownload } from '@/modules/issuer-dashboard/hooks/useGenerateTaxReportDownload.ts';
import {
  useRegisterTotalFundByYearRegisterHoldersQuery,
  useUpsertRegisterTotalFundByYearMutation,
} from '@/modules/issuer-dashboard/queries/tax-reports.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Avatar, Button, DisclosureListItem, FilterItem, SearchField, Spinner } from 'ui';
import { EmptyFolderIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { RegisterResponse } from 'api';

const OPEN_REGISTER_SEARCH_PARAM = 'openRegister';

interface IssuerTaxReportsRegisterItemProps {
  register: RegisterResponse;
  selectedYear: number;
  totalFund?: number | string;
}

const IssuerTaxReportsRegisterItem = ({
  register,
  selectedYear,
  totalFund,
}: IssuerTaxReportsRegisterItemProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const disclosureRef = useRef<HTMLDivElement>(null);
  const hasAutoOpenedRef = useRef<boolean>(false);
  const [totalFundInput, setTotalFundInput] = useState<string>(String(totalFund ?? 0));
  const { mutateAsync: upsertTotalFund, isLoading: isSavingTotalFund } =
    useUpsertRegisterTotalFundByYearMutation();
  const { generateTaxReport, isDownloading } = useGenerateTaxReportDownload();
  const scrollableId = `tax-reports-issuers-${register.id}`;

  useEffect(() => {
    setTotalFundInput(String(totalFund ?? 0));
  }, [totalFund]);

  const normalizedTotalFund = useMemo(() => {
    const parsed = Number(totalFundInput.replaceAll(',', '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }, [totalFundInput]);

  const isDirty = useMemo(() => {
    return normalizedTotalFund !== Number(totalFund ?? 0);
  }, [normalizedTotalFund, totalFund]);

  const isSaveDisabled = useMemo(() => {
    return !isDirty || isSavingTotalFund;
  }, [isDirty, isSavingTotalFund]);
  const isInitiallyOpen = useMemo(
    () => params.get(OPEN_REGISTER_SEARCH_PARAM) === String(register.id),
    [params, register.id],
  );
  const { data: registerHoldersData, isLoading: isLoadingRegisterHolders } =
    useRegisterTotalFundByYearRegisterHoldersQuery(
      { register_id: register.id, year: selectedYear },
      !isInitiallyOpen,
    );
  const holders = useMemo(() => registerHoldersData?.holders || [], [registerHoldersData?.holders]);
  const shouldRestoreOnMountRef = useRef<boolean | null>(null);
  if (shouldRestoreOnMountRef.current === null) {
    shouldRestoreOnMountRef.current = isInitiallyOpen;
  }

  useEffect(() => {
    if (shouldRestoreOnMountRef.current && isInitiallyOpen && !hasAutoOpenedRef.current) {
      disclosureRef.current?.click();
      hasAutoOpenedRef.current = true;
    }
  }, [isInitiallyOpen]);

  const handleSaveTotalFund = async () => {
    await upsertTotalFund({
      register_id: register.id,
      year: selectedYear,
      total_fund: normalizedTotalFund,
    });
  };
  const handleOpenEditTaxReport = useCallback(
    (entityId: number, holderDisplayName?: string) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set('year', String(selectedYear));
      const currentSearch = params.toString();
      const from = currentSearch ? `${pathname}?${currentSearch}` : pathname;

      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.taxReportHolderEdit(
            String(register.id),
            String(entityId),
          ),
          search: nextParams.toString(),
        },
        {
          state: { from, holderDisplayName },
        },
      );
    },
    [navigate, pathname, params, register.id, selectedYear],
  );

  const customBottomContent = (
    <>
      {!!holders.length && (
        <div id={scrollableId} className="overflow-y-auto max-h-[400px]">
          {holders.map(holder => {
            const displayName = holder.display_name || '-';
            const { firstName, lastName } = buildHolderAvatarNameParts(holder);

            return (
              <div
                key={holder.entity_id}
                className="w-full flex items-center justify-between px-2 sm:px-6 py-[14px] border-t border-gray-200"
              >
                <button
                  className="flex items-center gap-3"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenEditTaxReport(holder.entity_id, holder.display_name || undefined);
                  }}
                >
                  <Avatar
                    src={holder.avatar_url || undefined}
                    firstName={firstName}
                    lastName={lastName}
                    withBorder={true}
                  />
                  <p className="text-[14px] leading-[22px] font-medium">{displayName}</p>
                </button>
                <div className="flex items-center gap-2 pr-4 shrink-0">
                  <Button
                    variant="plain"
                    className="w-[158px] py-0 min-h-6 px-0"
                    onClick={e => {
                      e.stopPropagation();
                      handleOpenEditTaxReport(holder.entity_id, holder.display_name || undefined);
                    }}
                  >
                    Edit tax report
                  </Button>
                  <TaxReportDownloadDropdown
                    disabled={isDownloading}
                    onDownload={format =>
                      void generateTaxReport({
                        registerId: register.id,
                        entityId: holder.entity_id,
                        financialYear: selectedYear,
                        filename: `${displayName} Tax Report ${selectedYear}`,
                        format,
                      })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {isLoadingRegisterHolders && (
        <div className="h-[120px] flex items-center justify-center border-t border-gray-200">
          <Spinner size="md" />
        </div>
      )}
      {!isLoadingRegisterHolders && !holders.length && (
        <div className="flex flex-col justify-center items-center gap-6 h-[200px] border-t">
          <div className="flex items-center justify-center w-[36px] h-[36px] bg-[#E3E3E3] rounded-full">
            <img alt="no results" src={EmptyFolderIcon} className="w-4 h-4" />
          </div>
          <div className="text-xl text-neutral-900">No holders found for this register</div>
        </div>
      )}
    </>
  );

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[760px]">
        <DisclosureListItem
          ref={disclosureRef}
          onOpen={() => {
            const nextParams = new URLSearchParams(params);
            nextParams.set(OPEN_REGISTER_SEARCH_PARAM, String(register.id));
            setParams(nextParams, { replace: true });
          }}
          onClose={() => {
            if (params.get(OPEN_REGISTER_SEARCH_PARAM) !== String(register.id)) return;
            const nextParams = new URLSearchParams(params);
            nextParams.delete(OPEN_REGISTER_SEARCH_PARAM);
            setParams(nextParams, { replace: true });
          }}
          leftContent={
            <Avatar
              firstName={register.name}
              lastName={String(register.id)}
              src={register.icon_url}
              className="mr-2"
              withBorder={true}
              withRounded={false}
            />
          }
          leftMainString={register.name}
          leftSubduedContentSet={[`Type: ${fixNameEnum(register.type)}`]}
          classes={{
            leftContent: 'min-w-[200px] flex-1 max-w-[500px]',
            leftMainString:
              '!break-normal whitespace-normal [overflow-wrap:normal] [word-break:normal] leading-[22px]',
            rightContentUp: 'flex flex-nowrap justify-end gap-3',
          }}
          bottomCustomContent={customBottomContent}
          rightContent={
            <div
              className="w-full sm:w-auto flex items-center gap-3 pr-2 min-w-0"
              onClick={e => e.stopPropagation()}
            >
              <FilterItem
                type="custom"
                label="Total fund"
                className="w-[280px] md:w-[420px] min-w-[280px] h-10"
                classes={{ label: 'min-w-[120px]', element: 'w-full !py-0 !px-0' }}
              >
                <div className="w-full h-full px-3 flex items-center gap-1">
                  <span className={`${isDirty ? 'text-gray-900' : 'text-gray-400'}`}>$</span>
                  <SearchField
                    plain={true}
                    searchTimeout={0}
                    value={totalFundInput}
                    onChange={setTotalFundInput}
                    className={`!p-0 !h-auto text-sm leading-[22px] ${
                      isDirty ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  />
                </div>
              </FilterItem>
              <Button
                className="w-[96px] min-w-[96px]"
                loading={isSavingTotalFund}
                disabled={isSaveDisabled}
                onClick={handleSaveTotalFund}
              >
                Save
              </Button>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default IssuerTaxReportsRegisterItem;
